// ── NEAR-MATCH, BEFORE A MASTER ROW IS CREATED ───────────────────────────────
//
// Adding a payee, a head or any other master entry from a name somebody typed is how a
// register acquires two rows for one party. The register then looks fine — both rows are
// valid, both get used, and the exposure for that party is split across two names nobody
// reconciles. The suppliers table already carries this: SINGHAL TEXTILES on two tables,
// `mithila knit` and `MITHILA KNIT & CREATION`, `Fine threads` and `fine thread`,
// `superfine lables and packages` — every one typed past a picker that had the other.
//
// So the guard is not a validation. It is a QUESTION asked at the one moment it can still be
// answered cheaply: "did you mean this existing one?" It never refuses — a genuinely new
// vendor with a similar name must be addable — it only makes the collision visible.
'use strict';

// Compare on what a human would call the same name: case, punctuation and spacing removed.
// 'M/S SHEETAL ENTERPRISES' and 'ms sheetal enterprises' collapse to the same key.
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

// Tokens of 3+ characters, so 'and', 'co', '&' and initials do not create matches on their own.
const tokens = (s) => String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(t => t.length >= 3);

// Returns the existing entries a typed name might already be, most likely first.
//   exact    — same after normalisation. Almost certainly a duplicate.
//   contains — one name sits inside the other ('fine thread' in 'fine threads').
//   token    — they share every significant word, in any order.
function nearMatches(name, list, { nameOf = (x) => x.name, limit = 5 } = {}) {
  const n = norm(name);
  if (!n) return [];
  const nt = tokens(name);
  const out = [];
  for (const item of list || []) {
    const other = nameOf(item);
    const o = norm(other);
    if (!o) continue;
    if (o === n) { out.push({ item, name: other, why: 'exact', rank: 0 }); continue; }
    if (o.includes(n) || n.includes(o)) { out.push({ item, name: other, why: 'contains', rank: 1 }); continue; }
    const ot = tokens(other);
    if (nt.length && ot.length) {
      const shared = nt.filter((t) => ot.includes(t));
      // Every significant word of the shorter name appears in the longer one.
      if (shared.length && shared.length === Math.min(nt.length, ot.length)) {
        out.push({ item, name: other, why: 'same words', rank: 2 });
      }
    }
  }
  return out.sort((a, b) => a.rank - b.rank).slice(0, limit);
}

module.exports = { nearMatches, norm };
